import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { showToast } from "toast/toast";
import { ToastEventType } from "toast/types";

export interface ToastEvent {
    eventType: ToastEventType;
    message: string;
}

interface ToastContextValue {
    pushToastEvent: (event: ToastEvent) => void;
    currentToastEvent: ToastEvent | null;
    clearToastEvent: () => void;
    triggerToastEvent: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToastContext = (): ToastContextValue => {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error("useToastContext must be used inside a ToastContextProvider");
    }
    return ctx;
};

export const ToastContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentToastEvent, setCurrentToastEvent] = useState<ToastEvent | null>(null);
    const [triggerCalled, setTriggerCalled] = useState(false);

    const pushToastEvent = useCallback((event: ToastEvent) => {
        setCurrentToastEvent(event);
    }, []);

    const clearToastEvent = useCallback(() => {
        setCurrentToastEvent(null);
        setTriggerCalled(false); // Only reset BOTH when clearing
    }, []);

    const triggerToastEvent = useCallback(() => {
        setTriggerCalled(true);
    }, []);

    useEffect(() => {
        if (currentToastEvent && triggerCalled) {
            showToast(currentToastEvent.eventType, currentToastEvent.message);
            clearToastEvent();
        }
    }, [currentToastEvent, triggerCalled, clearToastEvent]);

    return (
        <ToastContext.Provider
            value={{
                pushToastEvent,
                currentToastEvent,
                clearToastEvent,
                triggerToastEvent,
            }}
        >
            {children}
        </ToastContext.Provider>
    );
};
